class RxkSubmenuRouter extends HTMLElement {
  #observer = new MutationObserver(records => {
    if (records.some(record => record.attributeName !== 'inert' || record.target.matches('.menu-list__submenu'))) this.route();
  });
  #order = new WeakMap();
  #nextOrder = 0;
  #header;
  #locales = [];
  #states = new Map();
  #events;
  #timer;
  #restoredResults = new WeakSet();

  connectedCallback() {
    this.#header = this.closest('header-menu');
    if (!this.#header) return;
    try { this.#locales = JSON.parse(this.dataset.locales || '[]'); } catch { this.#locales = []; }
    this.#events?.abort();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.#header.addEventListener('pointerover', this.#onPointerOver, options);
    this.#header.addEventListener('pointerout', this.#onPointerOut, options);
    this.#header.addEventListener('focusin', this.#onFocus, options);
    this.#header.addEventListener('click', this.#onPreviewClick, options);
    this.#header.addEventListener('keydown', this.#onPreviewKeydown, options);
    this.#header.addEventListener('submit', this.#onContactSubmit, { ...options, capture: true });
    this.#header.addEventListener('shopify:block:select', this.#onEditorSelect, { ...options, capture: true });
    this.route();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#events?.abort();
    clearTimeout(this.#timer);
    this.#states.clear();
  }

  #link(event) {
    return event.target instanceof Element ? event.target.closest('.rxk-menu-links a') : null;
  }

  #onPointerOver = (event) => {
    if (document.activeElement?.closest('.rxk-menu-contact')) return;
    const link = this.#link(event);
    if (!link || event.pointerType === 'touch' || link.contains(event.relatedTarget)) return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#preview(link), 160);
  };

  #onPointerOut = (event) => {
    const link = this.#link(event);
    if (link && !link.contains(event.relatedTarget)) clearTimeout(this.#timer);
  };

  #onFocus = (event) => {
    clearTimeout(this.#timer);
    const link = this.#link(event);
    if (link) this.#preview(link);
  };

  #onEditorSelect = (event) => {
    const config = event.target instanceof Element ? event.target.closest('[data-rxk-config]') : null;
    for (const state of this.#states.values()) {
      if (state.configs.includes(config)) this.#show(state, config);
    }
  };

  #onContactSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.rxk-menu-contact__form') || !form.checkValidity()) return;
    try { sessionStorage.setItem('rxk-contact-pending', JSON.stringify({ id: form.id, time: Date.now() })); } catch {}
  };

  #pendingContact() {
    try {
      const pending = JSON.parse(sessionStorage.getItem('rxk-contact-pending'));
      return pending && Date.now() - pending.time < 600000 ? pending.id : null;
    } catch { return null; }
  }

  #showContactFeedback(result) {
    if (!result.isConnected || document.querySelector('.rxk-contact-feedback')) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'rxk-contact-feedback';
    const message = result.cloneNode(true);
    message.removeAttribute('data-rxk-contact-result');
    message.id = 'RxkContactFeedbackMessage';
    dialog.setAttribute('aria-labelledby', message.id);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'button';
    close.textContent = result.dataset.closeLabel;
    close.addEventListener('click', () => dialog.close());
    dialog.append(message, close);
    dialog.addEventListener('close', () => {
      dialog.remove();
      if (this.#header.getClientRects().length) {
        result.dispatchEvent(new CustomEvent('rxk:contact-result', { bubbles: true }));
        // Opening the modal may have reset the menu to its default preview.
        const state = this.#states.get(result.closest('[data-rxk-slot]'));
        const config = result.closest('[data-rxk-config]');
        if (state?.configs.includes(config)) {
          this.#show(state, config);
          result.focus({ preventScroll: true });
        }
      }
    }, { once: true });
    document.body.append(dialog);
    dialog.showModal();
    message.focus();
    try { sessionStorage.removeItem('rxk-contact-pending'); } catch {}
  }

  #onPreviewClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-rxk-preview]') : null;
    const link = button?.previousElementSibling;
    if (!button || button.hidden || !link?.matches('a')) return;
    clearTimeout(this.#timer);
    this.#preview(link, true);
  };

  #firstControl(config) {
    return [...config.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])')]
      .find(element => element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }

  #onPreviewKeydown = (event) => {
    if (!(event.target instanceof Element) || !['Tab', 'ArrowRight'].includes(event.key)) return;
    const button = event.target.closest('[data-rxk-preview]');
    const state = this.#states.get(event.target.closest('[data-rxk-slot]'));
    if (!state) return;
    if (button && !event.shiftKey && (event.key === 'ArrowRight' || button.getAttribute('aria-expanded') === 'true')) {
      this.#preview(button.previousElementSibling);
      const control = this.#firstControl(state.active);
      if (control) { event.preventDefault(); control.focus(); }
    } else if (event.key === 'Tab' && event.shiftKey && event.target === this.#firstControl(state.active)) {
      const link = state.links.find(candidate => this.#key(candidate.href) === this.#key(state.active.dataset.rxkHoverTarget));
      const trigger = link?.nextElementSibling;
      if (trigger?.matches('[data-rxk-preview]') && !trigger.hidden) { event.preventDefault(); trigger.focus(); }
    }
  };

  #preview(link, toggle = false) {
    const slot = link.closest('[data-rxk-slot]');
    const state = this.#states.get(slot);
    if (!state || slot.closest('[inert]')) return;
    const config = state.configs.find(candidate => candidate.dataset.rxkHoverTarget &&
      this.#key(candidate.dataset.rxkHoverTarget) === this.#key(link.href));
    this.#show(state, toggle && config === state.active ? state.defaultConfig : config || state.defaultConfig);
  }

  #show(state, config) {
    state.active = config;
    for (const candidate of state.configs) {
      const inactive = candidate !== config;
      candidate.hidden = inactive;
      if (candidate.inert !== inactive) candidate.inert = inactive;
      if (inactive) candidate.setAttribute('aria-hidden', 'true');
      else candidate.removeAttribute('aria-hidden');
    }
    for (const link of state.links) {
      const active = !!config?.dataset.rxkHoverTarget && this.#key(config.dataset.rxkHoverTarget) === this.#key(link.href);
      link.toggleAttribute('data-rxk-preview-active', active);
      const button = link.nextElementSibling;
      if (button?.matches('[data-rxk-preview]')) button.setAttribute('aria-expanded', String(active));
    }
    this.#header.dispatchEvent(new Event('rxk:layoutchange'));
  }

  #key(value) {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      const parts = url.pathname.split('/').filter(Boolean);
      if (this.#locales.some(locale => locale.toLowerCase() === parts[0]?.toLowerCase())) parts.shift();
      return `${url.origin}/${parts.join('/')}`;
    } catch { return null; }
  }

  route() {
    this.#observer.disconnect();
    const header = this.#header;
    if (!header || !this.isConnected) return;
    const configs = [...header.querySelectorAll('[data-rxk-config]')];
    for (const config of configs) {
      if (!this.#order.has(config)) this.#order.set(config, this.#nextOrder++);
    }
    configs.sort((a, b) => this.#order.get(a) - this.#order.get(b));
    const used = new Set();
    const states = new Map();
    for (const slot of header.querySelectorAll('[data-rxk-slot]')) {
      const destination = this.#key(slot.dataset.rxkDestination);
      const mount = slot.querySelector('[data-rxk-mount]');
      const custom = slot.querySelector('[data-rxk-custom]');
      const fallback = slot.querySelector('[data-rxk-fallback]');
      if (!mount || !custom || !fallback) continue;
      const links = [...custom.querySelectorAll('.rxk-menu-links a')];
      const matching = configs.filter(candidate => !used.has(candidate) && destination &&
        candidate.dataset.rxkEnabled === 'true' &&
        this.#key(candidate.dataset.rxkDestination) === destination &&
        candidate.querySelector('.rxk-menu-tiles__link, .rxk-menu-contact form, .rxk-menu-hilfe__karte'));
      const config = matching.find(candidate => !candidate.dataset.rxkHoverTarget);
      const targets = new Set();
      const variants = config ? matching.filter(candidate => {
        const target = this.#key(candidate.dataset.rxkHoverTarget);
        if (!target || targets.has(target) || !links.some(link => this.#key(link.href) === target)) return false;
        targets.add(target);
        return true;
      }) : [];
      const mounted = config ? [config, ...variants] : [];
      mount.toggleAttribute('data-rxk-stable', mounted.length > 1);
      for (const link of links) {
        const button = link.nextElementSibling;
        if (!button?.matches('[data-rxk-preview]')) continue;
        const target = variants.find(candidate => this.#key(candidate.dataset.rxkHoverTarget) === this.#key(link.href));
        button.hidden = !target;
        if (target) button.setAttribute('aria-controls', target.id);
        else {
          button.removeAttribute('aria-controls');
          button.setAttribute('aria-expanded', 'false');
        }
      }
      for (const child of [...mount.children]) {
        if (!mounted.includes(child)) this.append(child);
      }
      if (config) {
        for (const candidate of mounted) {
          used.add(candidate);
          if (candidate.parentElement !== mount) mount.append(candidate);
        }
        const previous = this.#states.get(slot)?.active;
        const active = !slot.closest('[inert]') && mounted.includes(previous) ? previous : config;
        const state = { configs: mounted, defaultConfig: config, links, active };
        states.set(slot, state);
        this.#show(state, active);
      }
      custom.hidden = !config;
      fallback.hidden = !!config;
    }
    this.#states = states;
    for (const state of states.values()) {
      const result = state.configs.flatMap(config => [...config.querySelectorAll('[data-rxk-contact-result]')])
        .find(element => (`#${element.closest('form').id}` === location.hash ||
          element.closest('form').id === this.#pendingContact()) && !this.#restoredResults.has(element));
      if (result) {
        this.#restoredResults.add(result);
        const config = result.closest('[data-rxk-config]');
        customElements.whenDefined('header-menu').then(async () => {
          if (document.readyState !== 'complete') await new Promise(resolve => window.addEventListener('load', resolve, { once: true }));
          requestAnimationFrame(() => {
          if (!result.isConnected) return;
          this.#show(state, config);
          result.dispatchEvent(new CustomEvent('rxk:contact-result', { bubbles: true }));
          this.#showContactFeedback(result);
          });
        });
      }
    }
    for (const config of configs) {
      if (!used.has(config) && config.parentElement !== this) this.append(config);
    }
    this.#observer.observe(header, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-rxk-destination', 'data-rxk-enabled', 'data-rxk-hover-target', 'inert'] });
    header.dispatchEvent(new Event('rxk:layoutchange'));
  }
}

if (!customElements.get('rxk-submenu-router')) customElements.define('rxk-submenu-router', RxkSubmenuRouter);
